import React from 'react'
import { OmTabs } from '@owma/kit'

import i18n, { languageMap } from 'i18n'
import classNames from 'classnames'
import { i18Key } from 'utils'
import moment from 'moment'
import { UIGroup, ViewGroup } from 'utils/ui/propsUI'
import conf from 'conf'

import { confirmBase } from 'utils/ui'

import Logo from 'components/cleanui/layout/TopBar/Logo'
import settings from 'popups/Settings'

import chevronDown24 from 'assets/images/chevron-down-24.svg'
import logo128SVG from 'assets/images/logo/logo-128.svg'

import styles from './style.module.scss'
import { SettingsInterface } from '../def'

const { ko, en, ja } = languageMap

const langMap = {
  ko,
  en,
  ja,
}

const dateFormatList = ['YYYY-MM-DD', 'YYYY/MM/DD', 'YY/MM/DD']

const timeFormatList = ['HH:mm', 'h:mm A']

interface SettingItemProps {
  innerRef?: React.MutableRefObject<SettingsInterface | null>
  owner: any
  isMobile?: boolean
  parentLabel?: React.ReactNode
  idx?: string | number
}

interface SettingItemState {}

class SettingItem
  extends React.Component<SettingItemProps, SettingItemState>
  implements SettingsInterface
{
  state: SettingItemState = {}

  uiGroupMap: Record<string, any> = {}

  vGroupMap: Record<string, any> = {}

  isUICreated: boolean = false

  uiGroup: any = null

  aboutElem: React.ReactNode = null

  clickCount: number = 0

  componentDidMount(): void {
    const { innerRef } = this.props
    if (innerRef != null) {
      innerRef.current = this
    }

    this.applySettings()
  }

  // componentDidUpdate(prevProps) {
  //   const { locale } = this.props;
  //   if (prevProps != null && prevProps.locale !== locale) {
  //     console.log(' --- locale changed : ', locale, i18n.language);
  //     this.forceUpdate();
  //   }
  // }

  componentWillUnmount(): void {}

  createUIGroup = (): void => {
    if (this.isUICreated === true) {
      return
    }

    this.isUICreated = true

    this.createLocaleUIGroup()
    this.createAboutUIGroup()
  }

  createLocaleUIGroup = (): void => {
    const vGroups = [
      new ViewGroup({
        // titleKey: i18Key('settings.subtitle.locale'),
        desc: null,
      }),
    ]

    const date = moment(new Date())
    const timeStart = moment('01:01', 'HH:mm')
    const timeEnd = moment('23:59', 'HH:mm')

    const inputAttrs = {
      suffixIcon: <img src={chevronDown24} alt="menu" />,
    }

    const settingProps: any = {
      locale: {
        labelKey: i18Key('settings.fields.locale'),
        readOnly: false,
        format: 'om-select',
        default: 'ko-KR',
        values: Object.entries(langMap).map(([_key, value]) => {
          return { value: value.code, label: value.name }
        }),
        inputAttrs,
        vGroup: vGroups[0],
      },
      dateFormat: {
        labelKey: i18Key('settings.fields.dateFormat'),
        readOnly: false,
        format: 'om-select',
        default: dateFormatList[0],
        values: dateFormatList.map((value) => {
          return { value, labelKey: date.format(value) }
        }),
        inputAttrs,
        vGroup: vGroups[0],
      },
      timeFormat: {
        labelKey: i18Key('settings.fields.timeFormat'),
        readOnly: false,
        format: 'om-select',
        default: timeFormatList[0],
        values: timeFormatList.map((value) => {
          return { value, labelKey: `${timeStart.format(value)} - ${timeEnd.format(value)}` }
        }),
        inputAttrs,
        vGroup: vGroups[0],
      },
    }

    this.uiGroup = new UIGroup({
      children: settingProps,
      styles,
      reactObj: this,
    })

    this.vGroupMap.locale = vGroups

    this.uiGroupMap.locale = new UIGroup({
      children: settingProps,
      styles,
      reactObj: this,
    })
  }

  testLog = (): void => {
    const res: React.ReactNode[] = []
    res.push(<p> window.navigator : {window.navigator != null ? 'true' : 'false'} </p>)
    if (window.navigator != null) {
      res.push(<p> userAgent : {window.navigator.userAgent} </p>)
      const props = {
        appName: window.navigator['appName'],
        appVersion: window.navigator['appVersion'],
      }
      res.push(<p> navigator : {JSON.stringify(props)} </p>)
    }
    const content = <div>{res}</div>
    confirmBase({
      title: 'UserAgent',
      content,
    })
  }

  onClickCopyright = (): void => {
    if (this.clickCount == null) {
      this.clickCount = 0
    }
    this.clickCount++
    if (this.clickCount < 5) {
      return
    }
    this.clickCount = 0
    this.testLog()
  }

  createAboutUIGroup = (): void => {
    let dateStr = null
    if (!__ENV_PRODUCTION__) {
      const commitDate = moment(conf.gitInfo.commit.date).format('YYYY-MM-DD HH:mm:ss')
      dateStr = (
        <div className={classNames(styles.version)}>
          {commitDate} - {conf.gitInfo.commit.hash.substring(0, 7)}
        </div>
      )
    }

    this.aboutElem = (
      <div className={classNames(styles.about)}>
        <div className={classNames(styles.logo)}>
          <img src={logo128SVG} alt="logo" />
        </div>
        <div className={classNames(styles.logo2)}>
          <Logo alt="officemail" noWrapper={undefined} className={undefined} />
          <div className={classNames(styles.version)}>{import.meta.env.VITE_APP_VERSION}</div>
          {dateStr}
        </div>
        <div className={classNames(styles.copyright)} onClick={this.onClickCopyright}>
          {i18n.t('common.desc.copyright')}
        </div>
      </div>
    )
  }

  applySettings = (): void => {
    const clone = settings.getAll()
    // locale 은 local-only(localSettings.general.locale)로 분리됐다. 폼은 general 탭에 함께 표시하므로
    // dateFormat/timeFormat(general) + locale(localSettings.general) 을 하나로 합쳐 바인딩한다.
    const generalValues = {
      ...clone,
      ...(clone.general ?? {}),
      ...(clone.localSettings?.general ?? {}),
    }
    Object.keys(this.uiGroupMap).forEach((key) => {
      this.uiGroupMap[key].setValue(generalValues, true)
    })
  }

  onPropsChange = (key: string, value: any): void => {
    const { owner } = this.props

    if (owner != null && owner.onChangeValue != null) {
      owner.onChangeValue(`general.${key}`, value)
    }
  }

  getTabsAttrs = (): any => {
    const { owner, isMobile, parentLabel, idx } = this.props
    const { t } = i18n

    if (this.uiGroup == null) {
      this.createUIGroup()
    }

    let defaultActiveKey: string | null = null
    const { selectedTabs } = owner
    if (selectedTabs != null && selectedTabs.length > 1) {
      defaultActiveKey = selectedTabs[1]
    }

    const items = [
      {
        key: 'locale',
        label: (
          <div className={classNames('tabItem', 'locale', styles.locale)}>
            <div className={classNames(styles.label)}>{t('settings.generalTabs.locale')}</div>
          </div>
        ),
        children: (
          <div className={classNames('panel', 'locale', styles.locale)}>
            {this.uiGroupMap.locale.getElement()}
          </div>
        ),
      },
      {
        key: 'info',
        label: (
          <div className={classNames('tabItem', 'info', styles.info)}>
            <div className={classNames(styles.label)}>{t('settings.generalTabs.info')}</div>
          </div>
        ),
        children: <div className={classNames('panel', 'info', styles.info)}>{this.aboutElem}</div>,
      },
    ]

    const tabsAttrs: any = {
      className: classNames('subTabs'),
      items,
      centered: true,
      tabPosition: 'left',
      tabBarExtraContent: isMobile ? { left: parentLabel } : null,
      // onChange: (activeKey) => {
      // },
      onTabClick: (activeKey: string) => {
        const item = items.find((item) => item.key === activeKey)
        if (item != null) {
          const index = parseInt(idx as string, 10)
          owner.mobileViewObj.setKey(1, activeKey, [parentLabel, item.label], index)
        }
      },
    }

    if (defaultActiveKey != null) {
      tabsAttrs.defaultActiveKey = defaultActiveKey
    }

    return tabsAttrs
  }

  render(): React.ReactNode {
    const tabsAttrs = this.getTabsAttrs()

    return (
      <div className={classNames(styles.general)}>
        <OmTabs {...tabsAttrs} />
      </div>
    )
  }
}

export default SettingItem
